import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {AppConfigService} from './app-config.service';
import {HttpClient} from '@angular/common/http';
import {StorageService} from './storage.service';
import {PermissionsService} from './permissions.service';
import {PreferencesService} from './preferences.service';
import {EncrDecrService} from './encr-decr.service';
import {SysUserModel} from '../DataModels/sys-user-model';
import {BehaviorSubject, EMPTY, interval, Observable, of, Subscription, switchMap, timeout} from 'rxjs';
import {catchError, filter, take, tap, finalize, shareReplay} from 'rxjs/operators';
import {BsToastService} from './bs-toast-service';
import {jwtDecode} from 'jwt-decode';
import {RefreshTokenInfo, RootObjectTokenUser} from '../DataModels/auth.models';
import {RoutingTrackerService} from './routing-tracker.service';
import {OtpStartResponse, OtpVerifyRequest} from '../DataModels/otp-model';
import {ChatService} from './chat.service';
import {EventService} from './event-service';

/**
 * Service that handles user authentication and token management operations.
 */
@Injectable({providedIn: 'root'})
export class AuthService {
  // track number of auth instances created
  // private static _instances = 0;
  // private readonly _id = ++AuthService._instances;

  // Single-flight memoization for refresh calls
  private refreshInFlight$: Observable<RefreshTokenInfo> | null = null;

  // Public state streams (unchanged API)
  public refreshingTokens$ = new BehaviorSubject<boolean>(false);
  public isAccessTokenValid$ = new BehaviorSubject<boolean>(false);
  public isItTimeToRefreshToken$ = new BehaviorSubject<boolean>(false);
  public accessToken$ = new BehaviorSubject<string>('');

  // User observable placeholders maintained for compatibility
  public user: Observable<SysUserModel>;
  public userInfo: SysUserModel;
  public userInfoSubject: BehaviorSubject<SysUserModel>;

  private refreshTimerSub: Subscription | null = null;

  private _isRefreshing = false; // local guard in addition to refreshingTokens$
  private _startingTimer = false; // add to class fields
  private logoutInProgress = false;
  private failedLoginAttempts = 0; // client-driven OTP trigger after N failures
  private readonly crossTabLogoutEventKey = 'auth:logout:event';
  private readonly crossTabActivityTsKey = 'auth:activity:last-ts';
  private readonly activityWriteThrottleMs = 1000;
  private lastActivityWriteAt = 0;
  private readonly tabSessionId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  constructor(
    private router: Router,
    private appConfigService: AppConfigService,
    protected http: HttpClient,
    private storage: StorageService,
    private permissions: PermissionsService,
    private preferences: PreferencesService,
    private toast: BsToastService,
    private encryptDecrypt: EncrDecrService,
    private routingTracker: RoutingTrackerService,
    private chatService: ChatService,
    private eventService: EventService
  ) {
    // Seed user info BehaviorSubject from persisted storage
    this.userInfoSubject = new BehaviorSubject<SysUserModel>(
      JSON.parse(localStorage.getItem('currentUser'))
    );
    const persistedAccessToken = (this.userInfoSubject.value?.accessToken || '').trim();
    this.accessToken$.next(persistedAccessToken);
    this.isAccessTokenValid$.next(!!persistedAccessToken);
    this.initializeCrossTabLogoutSync();
    this.initializeWakeUpRefresh();
    // console.log('[AuthService] instance', this._id);
  }

  /**
   * Listens for the page becoming visible again (e.g. after device/browser sleep or tab switch).
   * When the app wakes up the interval timer may have been paused for an arbitrary duration,
   * so we immediately check the real-clock expiration and refresh/logout as needed.
   */
  private initializeWakeUpRefresh(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.isAuthenticated() && !this.logoutInProgress) {
        this.checkAndRefreshNow();
      }
    });
  }

  private initializeCrossTabLogoutSync(): void {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== this.crossTabLogoutEventKey || !event.newValue || this.logoutInProgress) {
        return;
      }

      let shouldTimeoutLogout = false;
      let propagatedMessage = '';

      try {
        const payload = JSON.parse(event.newValue) as {
          sourceTabId?: string;
          isTimeOut?: boolean;
          displayMessage?: string;
        };
        if (payload?.sourceTabId === this.tabSessionId) {
          return;
        }

        shouldTimeoutLogout = !!payload?.isTimeOut;
        propagatedMessage = payload?.displayMessage || '';
      } catch {
        // Ignore parse errors and still proceed with remote logout.
      }

      if (shouldTimeoutLogout) {
        localStorage.setItem('idleForcedLogout', '1');
      }

      this.logout(shouldTimeoutLogout, propagatedMessage, false);
    });
  }

  /**
   * Broadcasts a cross-tab logout event by storing the payload in local storage.
   * This method is used to notify other browser tabs about a logout event.
   *
   * @param {boolean} [isTimeOut=false] - Indicates if the logout is due to a timeout.
   * @param {string} [displayMessage=''] - An optional message to display during the logout event.
   * @return {void} This method does not return any value.
   */
  private broadcastCrossTabLogout(isTimeOut: boolean = false, displayMessage: string = ''): void {
    const payload = {
      sourceTabId: this.tabSessionId,
      timestamp: Date.now(),
      isTimeOut,
      displayMessage
    };
    localStorage.setItem(this.crossTabLogoutEventKey, JSON.stringify(payload));
  }

  /**
   * Checks if the user is authenticated by verifying the presence of a valid access token
   * stored in the local storage.
   *
   * @return {boolean} Returns `true` if the user is authenticated, otherwise `false`.
   */
  isAuthenticated(): boolean {
    const stored: SysUserModel = JSON.parse(localStorage.getItem('currentUser'));
    return !!stored?.accessToken;
  }

  public getAppUserInfo(): SysUserModel {
    const stored: SysUserModel = JSON.parse(localStorage.getItem('currentUser'));
    return stored;
  }


  public markSessionActivity(force: boolean = false): void {
    if (this.logoutInProgress || !this.isAuthenticated()) {
      return;
    }

    const now = Date.now();
    if (!force && now - this.lastActivityWriteAt < this.activityWriteThrottleMs) {
      return;
    }

    this.lastActivityWriteAt = now;
    localStorage.setItem(this.crossTabActivityTsKey, String(now));
  }

  public getLastSessionActivityAt(): number {
    const raw = localStorage.getItem(this.crossTabActivityTsKey);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  public isCrossTabSessionIdle(idleWindowMs: number): boolean {
    const lastActivityAt = this.getLastSessionActivityAt();
    if (!lastActivityAt) {
      return true;
    }

    return Date.now() - lastActivityAt >= idleWindowMs;
  }

  public getCrossTabActivityStorageKey(): string {
    return this.crossTabActivityTsKey;
  }

  // --- OTP state (optional 2FA) ---
  public otpRequired$ = new BehaviorSubject<boolean>(false);
  public otpDelivery$ = new BehaviorSubject<string>('');
  public otpMaskedTarget$ = new BehaviorSubject<string>('');
  public otpRef$ = new BehaviorSubject<string>('');
  public otpInProgress$ = new BehaviorSubject<boolean>(false);
  // Indicates the UI should prompt the user to start OTP (but do not auto-send)
  public otpPrompt$ = new BehaviorSubject<boolean>(false);

  private resetOtpState() {
    this.otpRequired$.next(false);
    this.otpDelivery$.next('');
    this.otpMaskedTarget$.next('');
    this.otpRef$.next('');
    this.otpInProgress$.next(false);
    this.otpPrompt$.next(false);
  }

  /**
   * Normalizes the provided value into a UTC string representation of an expiration date.
   *
   * @param {any} value The input value to be normalized. It can be a number (timestamp in milliseconds or seconds),
   *                    a string (numeric or date format), or any value that can be parsed into a date.
   *                    If the value is null, undefined, an empty string, or an invalid date, an empty string is returned.
   * @return {string} The UTC string representation of the date or an empty string if the input is invalid.
   */
  private normalizeExpirationToUtcString(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (typeof value === 'number') {
      const ms = value > 1e12 ? value : value * 1000;
      const date = new Date(ms);
      return Number.isNaN(date.getTime()) ? '' : date.toUTCString();
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        const numeric = Number(trimmed);
        if (!Number.isNaN(numeric)) {
          const ms = numeric > 1e12 ? numeric : numeric * 1000;
          const date = new Date(ms);
          return Number.isNaN(date.getTime()) ? '' : date.toUTCString();
        }
      }
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '' : date.toUTCString();
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toUTCString();
  }

  /**
   * Finalizes the login process for a given user by handling token validation, session persistence, and UI updates.
   *
   * @param {SysUserModel} user - The user object containing login details such as access token, refresh token, and username.
   * @return {boolean} Returns true if the login process is completed successfully, otherwise false.
   */
  private finalizeLoginFromUser(user: SysUserModel): boolean {
    // Normalize username casing from various backend shapes
    const resolvedUsername = (user as any)?.username || (user as any)?.userName || (user as any)?.UserName || (user as any)?.name || '';
    if (resolvedUsername) {
      (user as any).username = resolvedUsername;
    }
    this.userInfo = user;
    this.userInfoSubject.next(this.userInfo);
    this.logoutInProgress = false;

    // Emit token immediately so AppComponent (and others) can proceed
    this.accessToken$.next(user.accessToken);
    this.isAccessTokenValid$.next(!!user.accessToken);

    // Clear any idle forced logout message for future visits
    localStorage.removeItem('idleForcedLogout');

    try {
      // Decode JWT for expiration info
      const decodedToken: any = jwtDecode(this.userInfo.accessToken);
      this.userInfo.accessTokenExpiration = decodedToken.exp;
      this.userInfo.refreshTokenExpiration = this.normalizeExpirationToUtcString(
        this.userInfo.refreshTokenExpiration
      );

      // Persist user session
      localStorage.setItem('currentUser', JSON.stringify(this.userInfo));
      this.markSessionActivity(true);

      // Start background token maintenance - has to be here for login start and not in app.component
      this.startTokenAutoRefresh(30000);

      const accessToken: SysUserModel = JSON.parse(localStorage.getItem('currentUser'));
      if (accessToken?.accessToken) {
        this.getUserInfo();
      } else {
        this.toast.showWarningToast('Invalid or missing token. Access denied.');
        void this.logout();
        return false;
      }
    } catch (e) {
      console.error('Finalize login error:', e);
      this.toast.showWarningToast('Login failed due to invalid token.');
      void this.logout();
      return false;
    }
    return true;
  }

  /**
   * Handles the user login process by sending a request to the authentication API and managing the session upon success.
   * Returns true when fully logged in; false if an OTP step is required or on error.
   */
  async login(username: string, password: string): Promise<boolean> {
    // set token paths
    const tokenPath = this.appConfigService.tokenPath;
    const authPath = this.appConfigService.apiBaseUrl;

    console.log('User: ' + username + ' attempting login at: ' + new Date().toISOString());

    return new Promise<boolean>((resolve) => {
      this.http
        .post<any>(authPath + tokenPath + 'login', {username, password})
        .subscribe({
          next: (resp: any) => {
            // Case: server returned tokens directly
            if (resp && resp.accessToken) {
              const ok = this.finalizeLoginFromUser(resp as SysUserModel);
              this.resetOtpState();
              this.failedLoginAttempts = 0; // reset on success
              resolve(!!ok);
              return;
            }
            // Maintain backward compatibility if server still indicates OTP requirement
            if (resp && resp.otpRequired) {
              const otp = resp as OtpStartResponse;
              this.otpRequired$.next(true);
              this.otpRef$.next(otp.otpRef || '');
              this.otpDelivery$.next(otp.delivery || '');
              this.otpMaskedTarget$.next(otp.sentToMasked || '');
              this.otpInProgress$.next(true);
              resolve(false); // not fully logged in yet
              return;
            }

            // Unexpected response
            this.toast.showWarningToast('Unexpected login response.');
            resolve(false);
          },
          error: (err: any) => {
            console.error(err);
            console.log('User: ' + username + ' failed login at: ' + new Date().toISOString());

            this.toast.showWarningToast('Invalid login credentials.');

            // Client-driven OTP hint after 3 failed attempts (do NOT auto-send)
            this.failedLoginAttempts = (this.failedLoginAttempts || 0) + 1;
            if (this.failedLoginAttempts >= 3 && !this.otpInProgress$.value && !this.otpRequired$.value) {
              // Prompt the user to start OTP themselves
              this.otpPrompt$.next(true);
              this.toast.showInfoWhiteToast('Having trouble logging in? Click \'Login Using OTP\' I added to the Login form to request a One Time Password (OTP).');
            }

            resolve(false);
          },
        });
    });
  }

  /**
   * Returns the list of companies the currently authenticated user has access to,
   * for rendering a "switch company" dropdown without a fresh login.
   */
  getMyCompanies(): Promise<any[]> {
    const tokenPath = this.appConfigService.tokenPath;
    const authPath = this.appConfigService.apiBaseUrl;

    return new Promise<any[]>((resolve) => {
      this.http
        .get<any[]>(authPath + tokenPath + 'my-companies')
        .subscribe({
          next: (resp: any[]) => {
            console.log('[auth.service] GET my-companies response:', resp);
            resolve(resp || []);
          },
          error: (err: any) => {
            console.error(err);
            resolve([]);
          },
        });
    });
  }

  /**
   * Switches the currently authenticated user's active company context. Treated as a
   * mini re-login: a brand-new access/refresh token pair scoped to the new company is
   * issued and the existing session is finalized against it (same as a normal login).
   */
  async switchCompany(companyDatabaseId: number): Promise<boolean> {
    const tokenPath = this.appConfigService.tokenPath;
    const authPath = this.appConfigService.apiBaseUrl;

    return new Promise<boolean>((resolve) => {
      this.http
        .post<any>(authPath + tokenPath + 'switch-company', {companyDatabaseId})
        .subscribe({
          next: (resp: any) => {
            console.log('[auth.service] POST switch-company response:', resp);
            if (resp && resp.accessToken) {
              // Chat threads/messages (and the in-flight realtime hub connection) are scoped
              // to the company that was active when they were loaded. Switching companies
              // changes the backend data context (same userId, different company DB), so any
              // previously cached thread/message state becomes invalid - using it causes
              // 403/500 errors when the UI tries to fetch a thread that no longer resolves
              // under the new company context. Reset chat state (and the realtime connection)
              // before finalizing the new company's session so everything is reloaded fresh.
              this.chatService.resetState();
              const ok = this.finalizeLoginFromUser(resp as SysUserModel);
              resolve(!!ok);
              return;
            }
            this.toast.showWarningToast('Unexpected switch-company response.');
            resolve(false);
          },
          error: (err: any) => {
            console.error(err);
            this.toast.showWarningToast('Unable to switch company.');
            resolve(false);
          },
        });
    });
  }

  // Verify OTP to complete login
  /**
   * Verifies the one-time password (OTP) for the specified user by sending the OTP code to the server.
   *
   * @param {string} userName - The username of the user whose OTP is being verified.
   * @param {string} code - The OTP code to be verified.
   * @return {Promise<boolean>} A promise that resolves to true if the OTP is successfully verified, or false if the verification fails.
   */
  verifyOtp(userName: string, code: string): Promise<boolean> {
    const tokenPath = this.appConfigService.tokenPath;
    const authPath = this.appConfigService.apiBaseUrl;

    // server returns tokens on verify; otpRef not required
    const body: OtpVerifyRequest = {userName, code};

    return new Promise<boolean>((resolve) => {
      this.http.post<SysUserModel>(authPath + tokenPath + 'otp/verify', body).subscribe({
        next: (user) => {
          // Normalize username using server-preferred casing if present; fallback to input
          const fromServer = (user as any)?.username || (user as any)?.userName || (user as any)?.UserName;
          (user as any).username = fromServer || userName || '';
          const ok = this.finalizeLoginFromUser(user);
          this.resetOtpState();
          this.failedLoginAttempts = 0; // reset counter on success
          resolve(!!ok);
        },
        error: (err) => {
          console.error('verifyOtp error', err);
          this.toast.showWarningToast('The code is invalid or expired.');
          resolve(false);
        },
      });
    });
  }

  // Send or resend OTP (optional convenience)
  /**
   * Sends an OTP (One-Time Password) request for the provided username.
   *
   * @param {string} userName - The username for which the OTP should be generated.
   * @return {Promise<boolean>} A promise that resolves to true if the OTP was successfully sent, or false if there was an error.
   */
  sendOtp(userName: string): Promise<boolean> {
    const tokenPath = this.appConfigService.tokenPath;
    const authPath = this.appConfigService.apiBaseUrl;

    return new Promise<boolean>((resolve) => {
      this.http.post<any>(authPath + tokenPath + 'otp/request', {userName}).subscribe({
        next: (otp: Partial<OtpStartResponse>) => {
          this.otpRequired$.next(true);
          this.otpRef$.next(otp?.otpRef || '');
          // Default to email if delivery method not supplied
          this.otpDelivery$.next(otp?.delivery || this.otpDelivery$.value || 'email');
          this.otpMaskedTarget$.next(otp?.sentToMasked || this.otpMaskedTarget$.value || '');
          this.otpInProgress$.next(true);
          this.toast.showSuccessToast('A code has been sent to your email. Enter this code in the verification form.');
          resolve(true);
        },
        error: (err) => {
          console.error('sendOtp error', err);
          this.toast.showWarningToast('Failed to send security code.');
          resolve(false);
        },
      });
    });
  }

  /**
   * Checks whether the current user's token is expired or nearly expired.
   * Determines if the token should be refreshed based on its expiration time.
   *
   * @return {Observable<boolean>} An observable that emits a boolean value indicating whether it is time to refresh the token.
   */
  checkTokenExpired(): Observable<boolean> {
    try {
      const currentUserRaw = localStorage.getItem('currentUser');
      if (!currentUserRaw) {
        this.isItTimeToRefreshToken$.next(false);
        return of(false);
      }

      const user: Partial<SysUserModel & RootObjectTokenUser> = JSON.parse(currentUserRaw);
      const token = user?.accessToken;
      if (!token || typeof token !== 'string') {
        this.isItTimeToRefreshToken$.next(false);
        return of(false);
      }

      const decoded: any = jwtDecode(token);
      const expClaim = Number(decoded?.exp);
      if (!expClaim || Number.isNaN(expClaim)) {
        this.isItTimeToRefreshToken$.next(false);
        return of(false);
      }
      // Normalize exp to seconds if backend provided milliseconds
      const expSeconds = expClaim > 1e12 ? Math.floor(expClaim / 1000) : expClaim;

      const nowSeconds = Math.floor(Date.now() / 1000);
      const minutesLeft = (expSeconds - nowSeconds) / 60;
      // console.log('checkTokenExpired(): minutesLeft: ' + minutesLeft);

      // If the token is effectively expired or too close to expiry (< 0.3 min ~ 18s),
      // do NOT attempt a refresh. Auto-logout instead to avoid refresh calls with
      // already-expired refresh tokens when timers resume late.
      if (minutesLeft < 0.3) {
        this.isItTimeToRefreshToken$.next(false);
        // Trigger auto logout due to token expiration
        this.logout(true, 'Your session token has expired. Please log in again.');
        return of(false);
      }

      const shouldRefresh = minutesLeft < 15;
      this.isItTimeToRefreshToken$.next(shouldRefresh);
      return of(shouldRefresh);
    } catch (err) {
      console.error('checkTokenExpired():', err);
      this.isItTimeToRefreshToken$.next(false);
      return of(false);
    }
  }

  /**
   * Refreshes the access token and update user session information if valid refresh token and user info are provided.
   *
   * @param {any} [userInfo=null] - Optional object containing user information: `accessToken`, `refreshToken`, and `username`.
   *                                If not provided or incomplete, the method attempts to retrieve the information from
   *                                local storage.
   * @return {Observable<RefreshTokenInfo>} Returns an observable that emits `RefreshTokenInfo` upon successful token refresh,
   *                                        or `null` if the operation fails or no valid user info is available.
   */
  refreshToken(userInfo: any = null): Observable<RefreshTokenInfo> {
    if (this.logoutInProgress) {
      this.refreshingTokens$.next(false);
      return of(null as any);
    }
    // If a refresh is already in flight, return the shared observable
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    // Normalize/resolve user info: if not provided or incomplete, fallback to latest persisted user
    if (!userInfo || !userInfo.accessToken || !userInfo.refreshToken) {
      try {
        userInfo = JSON.parse(localStorage.getItem('currentUser') || 'null');
      } catch {
        userInfo = null;
      }
    }

    // Resolve username from common server variants
    const resolvedUsername = userInfo?.username || userInfo?.userName || userInfo?.UserName || userInfo?.name;

    // If still missing required fields, abort gracefully
    if (!userInfo || !userInfo.accessToken || !userInfo.refreshToken || !resolvedUsername) {
      this.refreshingTokens$.next(false);
      return of(null as any);
    }

    this.refreshingTokens$.next(true);

    const tokenPath = this.appConfigService.tokenPath;
    const authPath = this.appConfigService.apiBaseUrl + tokenPath;

    // Create a single-flight refresh request and share it among all subscribers
    this.refreshInFlight$ = this.http
      .post<any>(authPath + 'refresh', {
        accessToken: userInfo.accessToken,
        refreshToken: userInfo.refreshToken,
        username: resolvedUsername,
      })
      .pipe(
        tap((tokenInfo) => {
          if (this.logoutInProgress) {
            this.refreshingTokens$.next(false);
            return;
          }

          // Guard: backend may return null/empty on refresh when not authenticated
          if (!tokenInfo || !tokenInfo.accessToken || !tokenInfo.refreshToken) {
            this.refreshingTokens$.next(false);
            return;
          }

          console.log(
            'User: ' + resolvedUsername + ' refreshed token at: ' + new Date().toISOString()
          );

          // Update persisted user with fresh tokens
          let updatedUser: RootObjectTokenUser = null;
          try {
            updatedUser = (JSON.parse(localStorage.getItem('currentUser') || 'null') || userInfo) as RootObjectTokenUser;
          } catch {
            updatedUser = userInfo as RootObjectTokenUser;
          }
          updatedUser.accessToken = tokenInfo.accessToken;
          updatedUser.refreshToken = tokenInfo.refreshToken;
          updatedUser.expiration = tokenInfo.expiration;
          if (tokenInfo.refreshTokenExpiration) {
            updatedUser.refreshTokenExpiration = this.normalizeExpirationToUtcString(
              tokenInfo.refreshTokenExpiration
            );
          }
          // Normalize username casing for future refreshes
          (updatedUser as any).username = resolvedUsername;
          localStorage.setItem('currentUser', JSON.stringify(updatedUser));

          // Keep in-memory state in sync
          (this as any).user = updatedUser as any; // NOTE: this.user is typed Observable in legacy code
          this.userInfoSubject?.next(updatedUser as any);

          // Emit new token and validity after success
          // this.token = tokenInfo.accessToken;
          this.accessToken$.next(tokenInfo.accessToken);
          this.isAccessTokenValid$.next(!!tokenInfo.accessToken);
        }),
        finalize(() => {
          // Reset flags and allow new refresh attempts after completion
          this.refreshingTokens$.next(false);
          this.refreshInFlight$ = null;
        }),
        shareReplay(1)
      );

    return this.refreshInFlight$;
  }

  /**
   * Fetches the user information from the back-end API and performs a series of actions
   * based on the user data, such as loading permissions and preferences, storing encrypted
   * data locally, and managing routing upon successful login.
   *
   * @return {void} This method does not return a value. All operations are performed
   * internally, including API calls, error handling, and routing logic.
   */
  getUserInfo(): void {
    const endPointUrl = this.appConfigService.apiBaseUrl + 'sys_user/me';

    this.http
      .get<SysUserModel>(endPointUrl)
      .pipe(take(1))
      .subscribe({
        next: (user: SysUserModel) => {
          // Keep localStorage up to date with full user details including permissions and preferences
          const storedUserRaw = localStorage.getItem('currentUser');
          if (storedUserRaw) {
            const storedUser = JSON.parse(storedUserRaw);
            // Merge user info but always preserve token fields from the stored session —
            // sys_user/me does not return tokens and must never overwrite them.
            const mergedUser = {
              ...storedUser,
              ...user,
              accessToken: storedUser.accessToken,
              refreshToken: storedUser.refreshToken,
              accessTokenExpiration: storedUser.accessTokenExpiration,
              refreshTokenExpiration: storedUser.refreshTokenExpiration,
            };
            this.userInfo = mergedUser as SysUserModel;
            localStorage.setItem('currentUser', JSON.stringify(mergedUser));
            this.userInfoSubject.next(mergedUser);
          } else {
            this.userInfo = user;
          }
          if (!user.companyDbName) {
            this.toast.showErrorToast(
              'No company database has been assigned to this user. Contact your System Administrator.'
            );
            return;
          }
        },
        error: (e) => {
          console.error('Login error: ', e);
        },
        complete: () => {
          if (!this.userInfo?.companyDbName) {
            this.toast.showErrorToast(
              'No company database has been assigned to this user. Contact your System Administrator.'
            );
            return;
          }

          // Load/propagate permissions and preferences
          this.permissions.LoadPermissions(JSON.parse(this.userInfo.Permissions), 'auth service');
          this.preferences.LoadPreferences(JSON.parse(this.userInfo.Preferences));

          this.storage.StorageSet('Permissions', this.encryptDecrypt.encrypt(this.userInfo.Permissions));
          this.storage.StorageSet('Preferences', this.encryptDecrypt.encrypt(this.userInfo.Preferences));

          // Notify listeners (e.g. header component) that preferences are now available/updated,
          // so UI elements relying on them (such as the user avatar) refresh once this async
          // fetch completes. This fixes the first-run bug where the avatar was missing on initial
          // login because the header had already rendered before this data was ready.
          this.eventService.broadcast('PreferencesUpdated', null);

          console.log(
            'User: ' + this.userInfo.name + ' successfully logged in at: ' + new Date().toISOString()
          );

          // Route based on config/valid auth
          if (this.appConfigService.useLastPageAutoRouting && this.isAuthenticated()) {
            const lastPageViewed = localStorage.getItem('last_visited_url');
            if (lastPageViewed != null) {
              this.router.navigateByUrl(lastPageViewed).then();
            } else {
              this.router.navigateByUrl('/home').then();
            }
          } else if (this.isAuthenticated()) {
            this.router.navigateByUrl('/home').then();
          } else {
            void this.logout();
          }
        },
      });
  }

  /**
   * Logs out the current user from the application. It performs cleanup operations such as clearing user data
   * from storage, stopping token refresh, and navigating to the login page. Additionally, it can handle scenarios
   * where the logout is triggered due to session timeout or when a specific message needs to be displayed to the user.
   *
   * @param {boolean} [isTimeOut=false] - Indicates whether the logout was triggered due to session timeout.
   * @param {string} [displayMessage=''] - Optional message to display to the user upon logout.
   * @param broadcastToOtherTabs - tells the logout to other tabs
   * @return {void} This method does not return any value.
   */
  async logout(isTimeOut: boolean = false, displayMessage: string = '', broadcastToOtherTabs: boolean = true): Promise<void> {
    if (this.logoutInProgress) {
      return;
    }

    this.logoutInProgress = true;

    const user = this.currentUser();
    const logoutUserId = (user?.id ?? user?.Id ?? user?.userId ?? user?.UserId ?? '').toString();
    if (!isTimeOut) {
      console.log('User: ' + user?.username + ' logged out at: ' + new Date().toISOString());
    } else {
      console.log('User: ' + user?.username + ' session timed out at: ' + new Date().toISOString());
    }

    this.stopTokenAutoRefresh();

    this.chatService.endSession(logoutUserId)
      .pipe(take(1))
      .subscribe();

    // Ensure SignalR is disconnected and protocol-level teardown (stop) is complete
    // before we clear tokens and potentially abandon preflight requests.
    await this.chatService.disconnectRealtime();

    this.chatService.resetState();

    // Broadcast logout to token-driven subscribers (e.g., SignalR services)
    this.accessToken$.next('');
    this.isAccessTokenValid$.next(false);

    // Reset client-driven OTP state and counters
    this.failedLoginAttempts = 0;
    this.resetOtpState();
    this.otpPrompt$.next(false);

    const lastError = localStorage.getItem('lastErrorTrapped');
    const lastDateTime = localStorage.getItem('lastErrorDateTime');
    // Preserve idle timeout forced-logout flag across storage clear so login page can show message once
    // Also set it now if this logout is itself a timeout logout
    if (isTimeOut) {
      localStorage.setItem('idleForcedLogout', '1');
    }
    const idleForcedLogoutFlag = localStorage.getItem('idleForcedLogout');

    // Clear all persisted storage (localStorage, sessionStorage, in-memory cache)
    this.storage.Clear();
    localStorage.removeItem('currentUser');
    this.user = null as any;
    this.userInfoSubject.next(null);

    // Rehydrate last visited url for post‑login return, if appropriate
    let prevUrl = '';
    this.routingTracker.isPreviousUrl.subscribe((x) => {
      prevUrl = x?.valueOf();
      if (
        prevUrl != undefined &&
        prevUrl != '/login' &&
        prevUrl !== '/common/error500-page' &&
        prevUrl !== '/common/error404-page' &&
        prevUrl !== '/home'
      ) {
        localStorage.setItem('last_visited_url', prevUrl);
      }
    });

    // Restore last error diagnostic info if present
    localStorage.setItem('lastErrorTrapped', lastError);
    localStorage.setItem('lastErrorDateTime', lastDateTime);
    // Restore idle forced logout flag if it had been set by the idle timeout handler
    if (idleForcedLogoutFlag === '1') {
      localStorage.setItem('idleForcedLogout', '1');
    }

    if (broadcastToOtherTabs) {
      this.broadcastCrossTabLogout(isTimeOut, displayMessage);
    }

    this.router.navigateByUrl('/login').then(() => {
      // Reset guard only after navigation completes so concurrent logout calls
      // (e.g. cross-tab storage events during the async gap) are still blocked.
      this.logoutInProgress = false;
    });

    if (displayMessage !== '') {
      this.toast.showWarningToast(displayMessage);
    }
  }

  /**
   * Retrieves the current user information from localStorage.
   * If no user data is stored or an error occurs, it returns null.
   * Also updates the `user` field to keep it in sync with the latest data.
   *
   * @return {any | null} The latest user data as an object, or null if unavailable or if an error occurs.
   */
  public currentUser(): any | null {
    // Always prefer the latest from localStorage to avoid stale tokens
    try {
      const storedUser = localStorage.getItem('currentUser');
      if (!storedUser) {
        this.user = null as any;
        return null;
      }
      const latest = JSON.parse(storedUser);
      this.user = latest as any; // keep legacy field in sync
      return latest;
    } catch {
      return this.user as any ?? null;
    }
  }

  /**
   * Checks if the token has expired and refreshes it immediately if needed.
   * This method ensures that a token refresh occurs only if required and if no other refresh process is already ongoing.
   * It handles token refresh errors gracefully and logs them to the console.
   *
   * @return {void} Does not return any value.
   */
  public checkAndRefreshNow(): void {
    this.checkTokenExpired()
      .pipe(take(1))
      .subscribe((shouldRefresh) => {
        if (!shouldRefresh) {
          return;
        }
        const isAlreadyRefreshing = (this as any).refreshingTokens$?.value ?? this._isRefreshing;
        if (isAlreadyRefreshing) {
          return;
        }
        this._isRefreshing = true;
        this.refreshToken(this.currentUser())
          .pipe(
            timeout(20000),
            catchError((err) => {
              console.error('Immediate token refresh failed', err);
              return of(null);
            }),
            finalize(() => {
              this._isRefreshing = false;
            }),
            take(1)
          )
          .subscribe({
            next: () => {
            },
            error: () => {
            }
          });
      });
  }

  /**
   * Starts an automatic token refresh mechanism, periodically checking if the token should be refreshed
   * and performing the refresh operation when required. This method ensures that only one auto-refresh
   * timer runs at any given time and only initiates the process if a user is currently logged in.
   *
   * @param {number} [pollMs=30000] - The interval in milliseconds at which the token expiration is checked.
   * Defaults to 30000 milliseconds (30 seconds).
   * @return {void} This method does not return a value.
   */
  startTokenAutoRefresh(pollMs: number = 30000): void {
    // Prevent multiple timers
    if (this.refreshTimerSub && !this.refreshTimerSub.closed) {
      // console.log('token auto refresh timer already started');
      return;
    }
    if (this._startingTimer) {
      // console.log('token auto refresh timer is starting, skip');
      return;
    }

    // Only start if a user is currently logged in
    const currentUserRaw = localStorage.getItem('currentUser');
    if (!currentUserRaw) {
      // console.log('no user to auto refresh');
      return;
    }

    this._startingTimer = true; // enter critical section
    try {
      // console.log('token auto refresh timer started');
      const sub = interval(pollMs)
        .pipe(
          // Each tick: decide whether to refresh
          switchMap(() => this.checkTokenExpired().pipe(take(1))),
          filter((shouldRefresh) => shouldRefresh === true),
          // Avoid overlapping refresh calls
          switchMap(() => {
            const isAlreadyRefreshing = (this as any).refreshingTokens$?.value ?? this._isRefreshing;
            if (isAlreadyRefreshing) {
              return EMPTY;
            }
            this._isRefreshing = true;
            return this.refreshToken(this.currentUser()).pipe(
              timeout(20000),
              catchError((err) => {
                this.logout(true, 'Refresh of token failed / expired. Logging out..');
                console.error('Token refresh failed due to browser sleep causing Refresh Token expiry, or Token fetch failed', err.message);
                this._isRefreshing = false;
                return EMPTY;
              }),
              finalize(() => {
                this._isRefreshing = false;
              }),
              take(1)
            );
          })
        )
        .subscribe({
          next: () => {
            this._isRefreshing = false;
          },
          error: (e) => {
            this._isRefreshing = false;
            console.error('Auto-refresh stream error', e);
          },
        });

      // assign atomically near creation to narrow any race window
      this.refreshTimerSub = sub;
    } finally {
      this._startingTimer = false; // leave critical section
    }
  }

  /**
   * Stops the automatic token refresh mechanism.
   *
   * Cancels any existing token refresh timer subscription if active,
   * and sets relevant internal flags to indicate that the refreshing process
   * has been halted.
   *
   * @return {void} No return value.
   */
  stopTokenAutoRefresh(): void {
    if (this.refreshTimerSub) {
      // console.log('stopping token auto refresh');
      this.refreshTimerSub.unsubscribe();
      this.refreshTimerSub = null;
    }
    this._isRefreshing = false;
  }
}
